import React, { useRef } from 'react';
import { createPortal } from 'react-dom';
import IconButton from '../ui/IconButton';
import StatusPill from '../ui/StatusPill';
import useLayer from './useLayer';
import './overlays.css';

/**
 * Right-hand detail drawer — 440px, full height, slides over the content.
 * Row click opens it; the list stays in place behind.
 */
export default function Drawer({ open, onClose, title, status, statusTone, children, footer }) {
  const panelRef = useRef(null);
  useLayer({ open, onClose, ref: panelRef });

  if (!open) return null;

  return createPortal(
    <div className="sv-overlay sv-overlay--drawer" onMouseDown={onClose}>
      <aside
        ref={panelRef}
        className="sv-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : 'Details'}
        tabIndex={-1}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="sv-drawer__head">
          <div className="sv-drawer__title-wrap">
            <span className="sv-drawer__title sv-truncate">{title}</span>
            {status && <StatusPill status={status} tone={statusTone} />}
          </div>
          <IconButton icon="bx-x" size="sm" label="Close" onClick={onClose} />
        </header>

        <div className="sv-drawer__body">{children}</div>

        {footer && <footer className="sv-drawer__foot">{footer}</footer>}
      </aside>
    </div>,
    document.body
  );
}

/** Label/value row used throughout drawer bodies. */
export function DrawerField({ label, children, mono = false }) {
  return (
    <div className="sv-drawer-field">
      <span className="sv-drawer-field__label">{label}</span>
      <span className={`sv-drawer-field__value ${mono ? 'sv-mono' : ''}`}>
        {children == null || children === '' ? '—' : children}
      </span>
    </div>
  );
}

export function DrawerSection({ title, children }) {
  return (
    <section>
      {title && <h3 className="sv-section-heading">{title}</h3>}
      {children}
    </section>
  );
}
